/**
 * useSyncStatus Hook
 * Monitors network connectivity and Firebase sync status
 */

import { useState, useEffect } from 'react';
import { auth, db } from '@/config/firebase';
import { onSnapshot, collection, query, limit } from 'firebase/firestore';

/**
 * Custom hook for sync status
 * @returns {String} 'synced' | 'local' | 'syncing'
 */
export function useSyncStatus() {
  const [syncStatus, setSyncStatus] = useState('synced');
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Monitor network status
    function handleOnline() {
      setIsOnline(true);
    }

    function handleOffline() {
      setIsOnline(false);
      setSyncStatus('local');
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Monitor Firestore sync status
    let unsubscribe = null;
    
    const currentUser = auth.currentUser;
    if (currentUser && isOnline) {
      // Listen to a dummy collection to detect sync
      const testQuery = query(
        collection(db, 'users', currentUser.uid, 'transactions'),
        limit(1)
      );

      unsubscribe = onSnapshot(
        testQuery,
        (snapshot) => {
          // Successfully synced with Firestore
          if (!snapshot.metadata.fromCache) {
            setSyncStatus('synced');
          } else {
            setSyncStatus('local');
          }
        },
        (error) => {
          console.error('Sync status error:', error);
          setSyncStatus('local');
        }
      );
    } else if (!isOnline) {
      setSyncStatus('local');
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (unsubscribe) unsubscribe();
    };
  }, [isOnline]);

  // Detect when data is being written (syncing state)
  useEffect(() => {
    // This is a simplified implementation
    // In a production app, you'd track pending writes more precisely
    const handleBeforeUnload = () => {
      if (!isOnline) {
        setSyncStatus('local');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isOnline]);

  return syncStatus;
}

export default useSyncStatus;
